import HeaderSearch from './header-search';

export default class Header {
    constructor($header) {
        this.$header = $header;
        this.$panel = $header.querySelector('[data-header-panel]');
        this.$trigger = $header.querySelector('[data-header-menu-toggle]');
        this.$closeTrigger = $header.querySelector('[data-header-menu-close]');
        this.$promo = $header.querySelector('[data-header-promo]');
        this.$subMenuTriggers = $header.querySelectorAll('[data-trigger-submenu]');
        this.$backTriggers = $header.querySelectorAll('[data-trigger-back]');
        this.$menuWrappers = Array.from($header.querySelectorAll('[data-menu-wrapper]'));
        this.openSubMenu = this.openSubMenu.bind(this);

        this.initializePromo();

        if (!this.$panel || !this.$trigger || $header.dataset.headerInitialized) {
            return;
        }

        $header.dataset.headerInitialized = 'true';
        this.search = new HeaderSearch($header, () => this.close());
        this.syncMenuAccessibility();

        this.$trigger.addEventListener('click', () => this.toggle());
        this.$closeTrigger?.addEventListener('click', () => {
            this.close();
            this.$trigger.focus();
        });

        this.$subMenuTriggers.forEach($trigger => $trigger.addEventListener('click', this.openSubMenu));
        this.$backTriggers.forEach($back => {
            $back.addEventListener('click', this.goBack.bind(this, $back));
        });

        document.addEventListener('click', event => {
            if (this.isOpen() && !this.$header.contains(event.target)) {
                this.close();
            }
        });

        document.addEventListener('keydown', event => {
            if (!this.isOpen() || window.innerWidth >= 1400) {
                return;
            }
            if (event.key === 'Escape') {
                this.close();
                this.$trigger.focus();
            } else if (event.key === 'Tab') {
                this.trapFocus(event);
            }
        });
        window.addEventListener('resize', () => {
            if (window.innerWidth >= 1400 && this.isOpen()) {
                this.close();
            }

            this.syncMenuAccessibility();
        });
    }

    initializePromo() {
        if (!this.$promo || this.$promo.dataset.headerPromoInitialized || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const $track = this.$promo.querySelector('[data-header-promo-track]');
        const $items = $track?.querySelectorAll('[data-header-promo-item]');

        if (!$track || !$items || $items.length < 3) {
            return;
        }

        this.$promo.dataset.headerPromoInitialized = 'true';
        let activeIndex = 0;

        window.setInterval(() => {
            activeIndex += 1;
            $track.style.transform = `translateY(-${$items[activeIndex].offsetTop}px)`;

            if (activeIndex !== $items.length - 1) {
                return;
            }

            $track.addEventListener('transitionend', () => {
                $track.classList.add('header__promo-track--resetting');
                $track.style.transform = 'translateY(0)';
                activeIndex = 0;

                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        $track.classList.remove('header__promo-track--resetting');
                    });
                });
            }, {once: true});
        }, 4000);
    }

    isOpen() {
        return this.$panel.classList.contains('header__panel--open');
    }

    toggle() {
        this.search?.close();
        if (this.isOpen()) {
            this.close();
            return;
        }

        this.$panel.classList.add('header__panel--open');
        this.$trigger.classList.add('header__menu-toggle--open');
        this.$trigger.setAttribute('aria-expanded', 'true');
        this.$trigger.setAttribute('aria-label', this.$trigger.dataset.closeLabel);
        document.querySelector('html').classList.add('no-scroll');
        document.body.classList.add('header-menu-open');
        (this.$closeTrigger || this.$panel.querySelector('.header-menu__link, .header__phone'))?.focus();
    }

    close() {
        this.$header.querySelectorAll('[data-menu-class]').forEach($wrapper => this.setSubMenuOpen($wrapper.id, false));
        this.$subMenuTriggers.forEach($toggle => $toggle.setAttribute('aria-expanded', 'false'));
        this.$panel.classList.remove('header__panel--open');
        this.$trigger.classList.remove('header__menu-toggle--open');
        this.$trigger.setAttribute('aria-expanded', 'false');
        this.$trigger.setAttribute('aria-label', this.$trigger.dataset.openLabel);
        document.querySelector('html').classList.remove('no-scroll');
        document.body.classList.remove('header-menu-open');
    }

    openSubMenu(event) {
        if (window.innerWidth >= 1400) {
            return;
        }
        const $trigger = event.currentTarget;
        const menuId = $trigger.dataset.triggerSubmenu;
        const $wrapper = this.setSubMenuOpen(menuId, true);
        if (!$wrapper) {
            return;
        }
        $trigger.setAttribute('aria-expanded', 'true');
        $wrapper.querySelector('[data-trigger-back], a')?.focus({preventScroll: true});
    }

    setSubMenuOpen(menuId, isOpen) {
        const $wrapper = document.getElementById(menuId);
        if (!$wrapper) {
            return null;
        }
        const menuClass = $wrapper.dataset.menuClass;
        if (!menuClass) {
            return null;
        }
        $wrapper.classList.toggle(`${menuClass}__wrapper--open`, isOpen);
        $wrapper.querySelector('[data-menu-list]')?.classList.toggle(`${menuClass}--open`, isOpen);
        this.syncMenuAccessibility();
        return $wrapper;
    }

    goBack($back, event) {
        event.preventDefault();
        const menuId = $back.dataset.triggerBack;
        const $wrapper = this.setSubMenuOpen(menuId, false);
        if (!$wrapper) {
            return;
        }
        const $toggle = Array.from(this.$subMenuTriggers).find($trigger => $trigger.dataset.triggerSubmenu === menuId);
        $toggle?.setAttribute('aria-expanded', 'false');
        $toggle?.focus({preventScroll: true});
    }

    syncMenuAccessibility() {
        if (window.innerWidth >= 1400) {
            // Desktop submenus are disclosed with hover/focus, so mobile-only accessibility state must not block them.
            this.$menuWrappers.forEach($wrapper => {
                $wrapper.removeAttribute('inert');
            });
            return;
        }

        // Off-canvas submenu content remains in the DOM. `inert` keeps hidden links out of both the tab order and accessibility tree.
        this.$menuWrappers.forEach($wrapper => {
            const menuClass = $wrapper.dataset.menuClass;
            const isOpen = menuClass && $wrapper.classList.contains(`${menuClass}__wrapper--open`);
            $wrapper.toggleAttribute('inert', !isOpen);
        });
    }

    trapFocus(event) {
        const $activeMenu = this.$menuWrappers.filter($wrapper => {
            const menuClass = $wrapper.dataset.menuClass;
            return menuClass && $wrapper.classList.contains(`${menuClass}__wrapper--open`);
        }).at(-1);
        const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        // Append the close control after an active submenu so the custom focus order forms a predictable loop.
        const focusableElements = [
            ...Array.from(($activeMenu || this.$panel).querySelectorAll(focusableSelector)),
            ...($activeMenu ? [this.$closeTrigger] : [])
        ].filter(($element, index, elements) => {
            return $element
                && elements.indexOf($element) === index
                && !$element.closest('[inert]')
                && $element.getClientRects().length;
        });

        if (!focusableElements.length) {
            event.preventDefault();
            return;
        }

        const activeIndex = focusableElements.indexOf(document.activeElement);
        const lastIndex = focusableElements.length - 1;
        let targetIndex = null;

        if (activeIndex === -1) {
            targetIndex = event.shiftKey ? lastIndex : 0;
        } else if ($activeMenu && event.shiftKey && activeIndex === lastIndex) {
            targetIndex = lastIndex - 1;
        } else if ($activeMenu && !event.shiftKey && activeIndex === lastIndex - 1) {
            targetIndex = lastIndex;
        } else if (event.shiftKey && activeIndex === 0) {
            targetIndex = lastIndex;
        } else if (!event.shiftKey && activeIndex === lastIndex) {
            targetIndex = 0;
        }

        if (targetIndex !== null) {
            event.preventDefault();
            focusableElements[targetIndex].focus();
        }
    }
}
